import '../overlay.css';
import { ALERT_ANIMATIONS, ALERT_COLORS, ALERT_FONTS, ALERT_THEMES, pickEnum } from './style-values.js';

/**
 * One alert, drawn exactly as the overlay draws it (redesign Phase D).
 *
 * ==================== ONE RENDERER, TWO CALLERS ====================
 * The browser source and the editor preview both draw through this. That is
 * the whole point, and it is the lesson Widgets paid for: a preview built from
 * a separate mock is a DRAWING of an alert rather than the alert, and the two
 * drift the first time either changes — invisibly, because a mock always
 * renders successfully.
 *
 * It is PURE. No fetching, no token, no queue, no timers — those belong to
 * `OverlayAlertsPage`, which is the only caller holding a credential.
 * ===================================================================
 *
 * ==================== UNTRUSTED CONTENT ====================
 * Alert text is a template filled from a real event, and `{displayName}` is
 * whatever a viewer called themselves. It is a React text child and never
 * `dangerouslySetInnerHTML`: somebody whose name is markup appears on stream as
 * characters (§21, §57).
 * ===========================================================
 *
 * ==================== STYLE IS ALSO UNTRUSTED ====================
 * `style` arrives as JSON straight out of the `alert_definitions.style`
 * column (`packages/alerts/src/style.ts`'s `alertStyleSchema` only bounds it
 * on WRITE). Every field is read defensively here, exactly the way
 * `WidgetRender`'s `styleOf` reads a widget's — a `typeof` check with a
 * fallback, never a cast. An alert that vanishes because somebody wrote a bad
 * colour into that column is strictly worse than an ugly alert, so nothing
 * below ever throws or renders nothing on account of the style.
 *
 * `{}` — the column's default, and what every alert rendered before this
 * change was implicitly drawn with — resolves to exactly the values
 * `.overlay__alert` hard-coded: theme minimal, font system, white, no
 * animation, a shadow but no outline, 82% opaque, a 12px corner. That is not
 * a coincidence; it is the whole point of Task 2's defaults.
 *
 * The four enumerated fields (`theme`, `font`, `color`, `animation`) go
 * through `pickEnum` (`./style-values.js`), not a bare `typeof` check: a
 * `typeof === 'string'` test does not stop a string containing a SPACE, and
 * interpolating one bare into a class name would smuggle a second,
 * unconfigured class token in alongside the first (fix round 1).
 * ====================================================================
 */
export function AlertRender({
  text,
  style = {},
}: {
  text: string;
  style?: Record<string, unknown>;
}): React.JSX.Element {
  const scale = typeof style.fontScale === 'number' ? style.fontScale : 1;
  const opacity = typeof style.backgroundOpacity === 'number' ? style.backgroundOpacity : 0.82;
  const radius = typeof style.cornerRadius === 'number' ? style.cornerRadius : 12;

  const className = [
    'overlay__alert',
    `overlay__alert--theme-${pickEnum(style.theme, ALERT_THEMES, 'minimal')}`,
    `overlay__alert--font-${pickEnum(style.font, ALERT_FONTS, 'system')}`,
    `overlay__alert--color-${pickEnum(style.color, ALERT_COLORS, 'white')}`,
    `overlay__alert--anim-${pickEnum(style.animation, ALERT_ANIMATIONS, 'none')}`,
    style.textShadow === false ? '' : 'overlay__alert--shadow',
    style.textOutline === true ? 'overlay__alert--outline' : '',
  ]
    .filter((entry) => entry !== '')
    .join(' ');

  return (
    <div
      className={className}
      style={
        {
          '--alert-scale': scale,
          '--alert-bg-opacity': opacity,
          '--alert-radius': radius,
        } as React.CSSProperties
      }
    >
      {text}
    </div>
  );
}
